// Strategy constants for V2 templates
export const V2_STRATEGIES = {
  AUTO_INCREMENT: 'auto_increment_strategy',
  FULL_INGEST: 'full_ingest_strategy',
  INCREMENTAL_V1: 'incremental_v1_strategy',
} as const;

// Strategy display information
export const STRATEGY_DISPLAY_INFO = {
  [V2_STRATEGIES.AUTO_INCREMENT]: {
    label: 'Auto Increment Strategy',
    value: V2_STRATEGIES.AUTO_INCREMENT,
    description: 'Automatically detects and syncs new records based on incremental IDs',
    shortLabel: 'Auto Increment',
  },
  [V2_STRATEGIES.FULL_INGEST]: {
    label: 'Full Ingest Strategy',
    value: V2_STRATEGIES.FULL_INGEST,
    description: 'Downloads all data on each sync cycle',
    shortLabel: 'Full Ingest',
  },
  [V2_STRATEGIES.INCREMENTAL_V1]: {
    label: 'Incremental V1 Strategy',
    value: V2_STRATEGIES.INCREMENTAL_V1,
    description: 'Uses V1-style incremental sync with timestamp-based detection',
    shortLabel: 'Incremental V1',
  },
} as const;

// Strategy types for type safety
export type V2StrategyType = (typeof V2_STRATEGIES)[keyof typeof V2_STRATEGIES];

// Helper functions
export const getStrategyLabel = (strategy: V2StrategyType): string => {
  return STRATEGY_DISPLAY_INFO[strategy]?.label || strategy;
};

export const getStrategyDescription = (strategy: V2StrategyType): string => {
  return STRATEGY_DISPLAY_INFO[strategy]?.description || '';
};

export const getStrategyShortLabel = (strategy: V2StrategyType): string => {
  return STRATEGY_DISPLAY_INFO[strategy]?.shortLabel || strategy;
};

export const getAllStrategies = (): V2StrategyType[] => {
  return Object.values(V2_STRATEGIES);
};

export const getDefaultStrategy = (): V2StrategyType => {
  return V2_STRATEGIES.AUTO_INCREMENT;
};

// Strategy validation
export const isValidStrategy = (strategy: string): strategy is V2StrategyType => {
  return Object.values(V2_STRATEGIES).includes(strategy as V2StrategyType);
};

// Strategy grouping (for UI organization)
export const STRATEGY_GROUPS = {
  INCREMENTAL: [V2_STRATEGIES.AUTO_INCREMENT, V2_STRATEGIES.INCREMENTAL_V1],
  FULL_SYNC: [V2_STRATEGIES.FULL_INGEST],
} as const;

// Get strategies by group
export const getStrategiesByGroup = (group: keyof typeof STRATEGY_GROUPS): V2StrategyType[] => {
  return STRATEGY_GROUPS[group];
};
